import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";

const CARDS = [
  ["Home", "/api/card/home.png"],
  ["FIFA ranking", "/api/card/ranking.png"],
  ["Ranking ambition", "/api/card/ambition.png"],
  ["Football economy", "/api/card/economy.png"],
  ["League table", "/api/card/league.png"],
  ["Harambee fixtures", "/api/card/fixtures.png"],
];

export default function SearchConsole() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/search").then((r) => setState(r.data)).catch((e) => toast.error(errText(e)));
  }, []);

  useEffect(load, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/search/submit");
      toast[data.accepted ? "success" : "error"](
        data.accepted
          ? `${data.submitted_urls} URLs pushed to Bing, Yandex, Seznam and Naver`
          : "The push was refused — check the key file is live on the domain",
      );
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-search-console">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl uppercase tracking-tight text-white">Search engines</h1>
          <p className="mt-2 text-sm text-zinc-500">
            Rebuild the sitemap from the live records and push every page to the engines that accept a push.
          </p>
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid="search-submit-all"
          className="inline-flex items-center gap-2 bg-maasai px-5 py-3 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white disabled:opacity-50"
        >
          <Send size={13} /> {busy ? "Pushing…" : "Submit every page"}
        </button>
      </div>

      {state ? (
        <>
          <div className="grid gap-px bg-zinc-900 sm:grid-cols-2 lg:grid-cols-4" data-testid="search-summary">
            {[
              ["URLs in the sitemap", state.sitemap_urls],
              ["Push engines", state.indexnow_engines.length],
              ["Last push", state.last_submission ? new Date(state.last_submission.submitted_at).toLocaleString("en-GB") : "—"],
              ["Last push accepted", state.last_submission ? (state.last_submission.accepted ? "Yes" : "No") : "—"],
            ].map(([label, value]) => (
              <div key={label} className="bg-[#0b0b0b] p-5">
                <p className="text-[0.6rem] uppercase tracking-[0.16em] text-zinc-500">{label}</p>
                <p className="mono mt-2 text-sm font-bold text-ochre">{value}</p>
              </div>
            ))}
          </div>

          <section data-testid="search-google-steps">
            <h2 className="font-display text-lg uppercase tracking-tight text-ochre">Google — the manual part</h2>
            <p className="mt-3 text-sm text-zinc-400">
              Google stopped accepting sitemap pings, so verification and the sitemap submission are done once by
              hand. Everything else here is automatic afterwards.
            </p>
            <ol className="mt-4 space-y-3">
              {state.google.manual_steps.map((s, i) => (
                <li key={i} className="flex gap-3 border border-zinc-900 bg-[#0b0b0b] p-4 text-sm text-zinc-300">
                  <span className="mono text-ochre">{i + 1}</span>
                  {s}
                </li>
              ))}
            </ol>
            <div className="mt-4 flex flex-wrap gap-3">
              {[["Search Console", state.google.console_url], ["Bing Webmaster", state.bing.webmaster_url],
                ["Our sitemap", state.sitemap_url], ["IndexNow key file", state.indexnow_key_url]].map(([label, url]) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`search-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-2 border border-zinc-700 px-4 py-2 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white hover:border-ochre hover:text-ochre"
                >
                  {label} <ExternalLink size={12} />
                </a>
              ))}
            </div>
            <p className="mt-4 border-l-2 border-ochre bg-[#0b0b0b] p-4 text-xs leading-relaxed text-zinc-400">
              Meta-tag verification: put the codes in <span className="mono text-ochre">frontend/.env</span> as
              REACT_APP_GOOGLE_SITE_VERIFICATION and REACT_APP_BING_SITE_VERIFICATION and redeploy. File
              verification: run <span className="mono text-ochre">python3 backend/scripts/add_verification_file.py &lt;filename&gt;</span>.
            </p>
          </section>

          <section data-testid="search-cards">
            <h2 className="font-display text-lg uppercase tracking-tight text-ochre">Per-page share cards</h2>
            <p className="mt-3 text-sm text-zinc-400">
              Each of these is drawn live from that page's own figures. Player and county pages get one each at
              /api/card/player/&lt;slug&gt;.png and /api/card/county/&lt;slug&gt;.png.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {CARDS.map(([label, path]) => (
                <a
                  key={path}
                  href={`${process.env.REACT_APP_BACKEND_URL}${path}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`search-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border border-zinc-900 bg-[#0b0b0b] p-3 hover:border-ochre"
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${path}`}
                    alt={`${label} share card`}
                    className="w-full"
                    loading="lazy"
                  />
                  <p className="mt-3 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-zinc-400">{label}</p>
                </a>
              ))}
            </div>
          </section>

          {state.last_submission?.batches?.length ? (
            <section data-testid="search-last-batches">
              <h2 className="font-display text-lg uppercase tracking-tight text-ochre">Last push, batch by batch</h2>
              <div className="mt-4 border border-zinc-900">
                {state.last_submission.batches.map((b, i) => (
                  <div key={i} className="flex flex-wrap gap-4 border-b border-zinc-900 px-4 py-3 last:border-0 text-xs">
                    <span className="mono text-ochre">HTTP {b.status}</span>
                    <span className="text-zinc-400">{b.urls} URLs</span>
                    <span className="min-w-0 flex-1 truncate text-zinc-500">{b.body || "accepted"}</span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
